import React, { useEffect, useRef, useState } from 'react'
import { Checkbox } from '../../ui'
import { AssigneeChip } from './chips/AssigneeChip.jsx'
import { DueAgeDisplay } from './chips/DueAgeDisplay.jsx'
import { PriorityChip } from './chips/PriorityChip.jsx'
import { StatusChip } from './chips/StatusChip.jsx'

/**
 * Single task row. Composes the four chips, the title input, and the
 * hover-revealed delete button.
 *
 * In snapshot mode every interaction is disabled and the title is
 * rendered as plain text.
 */
export function TaskRow({
  task,
  reportId,
  managerId,
  reportName,
  managerName,
  currentUserId,
  disabled = false,
  closed = false,
  selectionMode = false,
  selected = false,
  onToggleSelected,
  onCycleStatus,
  onCyclePriority,
  onCycleAssignee,
  onSetDueDate,
  onUpdateTitle,
  onDelete,
}) {
  const [hovered, setHovered] = useState(false)
  const [editingTitle, setEditingTitle] = useState(false)
  const [draftTitle, setDraftTitle] = useState(task.title)
  const inputRef = useRef(null)

  useEffect(() => {
    setDraftTitle(task.title)
  }, [task.title])

  useEffect(() => {
    if (editingTitle) {
      inputRef.current?.focus()
      inputRef.current?.select()
    }
  }, [editingTitle])

  function commitTitle() {
    setEditingTitle(false)
    const next = draftTitle.trim()
    if (!next || next === task.title) {
      setDraftTitle(task.title)
      return
    }
    onUpdateTitle?.(next)
  }

  function cancelEdit() {
    setDraftTitle(task.title)
    setEditingTitle(false)
  }

  return (
    <div
      role="listitem"
      data-testid={`task-row-${task.taskId}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 4px',
        borderBottom: '1px solid var(--border)',
        background: hovered && !disabled ? 'var(--bg)' : 'transparent',
        transition: 'background var(--duration-fast) ease',
        borderLeft: editingTitle ? '2px solid var(--accent-link)' : '2px solid transparent',
      }}
      aria-disabled={disabled || undefined}
    >
      {selectionMode && (
        <Checkbox
          checked={selected}
          onChange={(e) => {
            e.stopPropagation()
            onToggleSelected?.()
          }}
          onClick={(e) => e.stopPropagation()}
          aria-label={`Select task: ${task.title}`}
          // No label → primitive renders a bare input, so we set the
          // 14×14 sizing here to match the rest of the row chrome.
          style={{ width: 14, height: 14, margin: 0, flexShrink: 0 }}
        />
      )}
      <StatusChip
        status={task.status}
        onCycle={onCycleStatus}
        disabled={disabled}
      />

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, gap: 2 }}>
        {editingTitle && !disabled ? (
          <input
            ref={inputRef}
            type="text"
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value)}
            onBlur={commitTitle}
            onKeyDown={(e) => {
              if (e.key === 'Enter') inputRef.current?.blur()
              if (e.key === 'Escape') cancelEdit()
            }}
            maxLength={200}
            aria-label="Task title"
            style={{
              fontSize: 13,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              color: 'var(--text-primary)',
              padding: 0,
              minWidth: 0,
            }}
          />
        ) : (
          <button
            type="button"
            onClick={() => !disabled && setEditingTitle(true)}
            disabled={disabled}
            aria-label={disabled ? `Task: ${task.title}` : `Edit task title: ${task.title}`}
            style={{
              all: 'unset',
              fontSize: 13,
              color: closed ? 'var(--text-secondary, #888)' : 'var(--text-primary)',
              textDecoration: closed ? 'line-through' : 'none',
              cursor: disabled ? 'default' : 'text',
              textAlign: 'left',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {task.title}
          </button>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <PriorityChip
            priority={task.priority}
            onCycle={onCyclePriority}
            disabled={disabled}
          />
          <DueAgeDisplay
            task={task}
            disabled={disabled}
            onSetDueDate={onSetDueDate}
          />
        </div>
      </div>

      <AssigneeChip
        assigneeId={task.assignee}
        reportId={reportId}
        managerId={managerId}
        reportName={reportName}
        managerName={managerName}
        currentUserId={currentUserId}
        embeddedName={task.assigneeName}
        isOrphaned={task.isOrphaned}
        onCycle={onCycleAssignee}
        disabled={disabled}
      />

      {!disabled && hovered && (
        <button
          type="button"
          onClick={onDelete}
          aria-label="Delete task"
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--text-secondary, #aaa)',
            fontSize: 16,
            lineHeight: 1,
            padding: '0 4px',
            flexShrink: 0,
          }}
        >
          ×
        </button>
      )}
    </div>
  )
}
